import React, { useEffect, useRef, useState } from 'react';
import { X, Printer, Loader2, FileText, Receipt, PackageSearch, Mail, CheckCircle2 } from 'lucide-react';
import './DocumentViewerModal.css';

type DocumentKind = 'ticket' | 'invoice' | 'order';

interface DocumentViewerModalProps {
  kind: DocumentKind;
  title: string;
  subtitle?: string;
  /** Devuelve el HTML del documento, el mismo que se manda a imprimir. */
  load: () => Promise<string>;
  /** Imprime el documento. Si lanza, el mensaje se enseña en el visor. */
  onPrint: () => Promise<void>;
  /**
   * Si se pasa, aparece un boton para mandar el documento por email. El visor no
   * sabe a quien: quien lo pasa se encarga de pedir la direccion.
   */
  onEmail?: () => void;
  /**
   * Estado del envio, para dar feedback en el boton: 'sending' muestra un
   * loader y 'sent' un "Enviado". Lo lleva quien manda el correo, porque el
   * envio puede empezar despues de pedir una direccion.
   */
  emailStatus?: 'idle' | 'sending' | 'sent';
  onClose: () => void;
}

/**
 * Margen que solo existe al imprimir (@page): en pantalla la factura saldria
 * pegada al borde, asi que el visor se lo pone.
 */
const A4_PREVIEW = 'html{background:#fff} body{padding:15mm;box-sizing:border-box}';

const PREVIEW_STYLE: Record<DocumentKind, string> = {
  ticket: 'html{background:#fff}',
  invoice: A4_PREVIEW,
  order: A4_PREVIEW
};

/**
 * Visor de documentos: enseña el ticket o la factura tal como saldran impresos,
 * sin imprimirlos. El HTML lo generan las mismas plantillas que la impresion.
 */
export function DocumentViewerModal({ kind, title, subtitle, load, onPrint, onEmail, emailStatus = 'idle', onClose }: DocumentViewerModalProps) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [frameHeight, setFrameHeight] = useState<number>(0);
  const [isPrinting, setIsPrinting] = useState(false);
  const requestId = useRef(0);

  useEffect(() => {
    const id = ++requestId.current;
    setHtml(null);
    setError(null);
    load()
      .then(result => { if (id === requestId.current) setHtml(result.replace('</head>', `<style>${PREVIEW_STYLE[kind]}</style></head>`)); })
      .catch(err => { if (id === requestId.current) setError(err?.message || 'No se pudo cargar el documento.'); });
    return () => { requestId.current++; };
  }, []);

  const handlePrint = async () => {
    if (isPrinting) return;
    setIsPrinting(true);
    try {
      await onPrint();
    } catch (err) {
      setError((err as any)?.message || 'No se pudo imprimir.');
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <div className="al-modal-root dv-root">
      <div className="al-modal-backdrop" onClick={onClose} />
      <div className={`dv-card dv-card--${kind}`}>
        <div className="dv-header">
          <div className="dv-header-left">
            <div className="dv-icon">{kind === 'ticket' ? <Receipt size={22} /> : kind === 'order' ? <PackageSearch size={22} /> : <FileText size={22} />}</div>
            <div>
              <h3 className="dv-title">{title}</h3>
              {subtitle && <p className="dv-subtitle">{subtitle}</p>}
            </div>
          </div>
          <button onClick={onClose} className="dv-close" aria-label="Cerrar"><X size={22} /></button>
        </div>

        <div className="dv-body">
          {error ? (
            <p className="dv-error" role="alert">{error}</p>
          ) : html === null ? (
            <div className="dv-loading"><Loader2 size={32} className="dv-spin" /><span>Cargando documento…</span></div>
          ) : (
            <iframe
              title={title}
              srcDoc={html}
              sandbox="allow-same-origin"
              className={`dv-frame dv-frame--${kind}`}
              style={frameHeight ? { height: frameHeight } : undefined}
              onLoad={e => {
                const doc = e.currentTarget.contentDocument;
                if (doc && doc.documentElement) setFrameHeight(doc.documentElement.scrollHeight);
              }}
            />
          )}
        </div>

        <div className="dv-footer">
          <button onClick={onClose} className="dv-btn dv-btn--secondary">Cerrar</button>
          {onEmail && (
            <button
              onClick={onEmail}
              disabled={html === null || emailStatus === 'sending'}
              className="dv-btn dv-btn--secondary"
              data-testid="dv-email"
            >
              {emailStatus === 'sending' ? <Loader2 size={18} className="dv-spin" />
                : emailStatus === 'sent' ? <CheckCircle2 size={18} color="#16A34A" />
                : <Mail size={18} />}
              {emailStatus === 'sending' ? 'Enviando…' : emailStatus === 'sent' ? 'Enviado' : 'Enviar por email'}
            </button>
          )}
          <button onClick={handlePrint} disabled={html === null || isPrinting} className="dv-btn dv-btn--primary">
            <Printer size={18} />
            {isPrinting ? 'Enviando…' : 'Imprimir'}
          </button>
        </div>
      </div>
    </div>
  );
}
